import React from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import FormulaResult from './FormulaResult';
import { getDisplayTestValue, type FormulaTestValues } from './formulaUtils';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';

export type FormulaToken =
    | { type: 'question'; ref: string; questionIndex: number; question: CampaignQuestion }
    | { type: 'operator'; text: string };

interface FormulaValidatorProps {
    isValid: boolean;
    tokens: FormulaToken[];
    validationError?: string;
    calculatedResult?: number | null;
    testMode?: boolean;
    maxScore?: number | null;
    testValues?: FormulaTestValues;
    isCalculating?: boolean;
    calculationError?: boolean;
}

/**
 * Displays the formula validation result with question chips and operators.
 *
 * @param {FormulaValidatorProps} props
 * @returns {JSX.Element | null}
 */
const FormulaValidator: React.FC<FormulaValidatorProps> = ({
    isValid,
    tokens,
    validationError,
    calculatedResult,
    testMode = false,
    testValues = {},
    isCalculating = false,
    calculationError = false,
}) => {
    if (tokens.length === 0) {
        return null;
    }

    const icon = isValid ? (
        <CheckCircle sx={{ fontSize: '1rem', color: 'success.main' }} />
    ) : (
        <ErrorOutline sx={{ fontSize: '1rem', color: 'error.main' }} />
    );

    return (
        <Box sx={{ borderTop: '1px solid', borderTopColor: 'divider', pt: 1, mt: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.75,
                }}
            >
                {icon}
                <Typography variant="subtitle2" fontWeight={500}>
                    Formula preview
                </Typography>
            </Box>
            {validationError && (
                <Typography variant="body2" color="error.main" sx={{ mb: 0.75 }}>
                    {validationError}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.15,
                    flexWrap: 'wrap',
                }}
            >
                {tokens.map((token, index) => {
                    if (token.type === 'question') {
                        const testValue = testValues[token.questionIndex];
                        const displayValue =
                            testMode && testValue !== undefined ? getDisplayTestValue(token.question, testValue) : token.ref;

                        return (
                            <Box
                                key={`${token.ref}-${index}`}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    bgcolor: 'devblue.90',
                                    borderRadius: 1,
                                    color: 'white.100',
                                    px: 0.5,
                                    py: 0.25,
                                    fontWeight: 600,
                                    fontFamily: testMode && testValue !== undefined ? 'monospace' : 'inherit',
                                }}
                            >
                                {displayValue}
                            </Box>
                        );
                    } else {
                        return (
                            <Typography
                                key={`op-${index}`}
                                variant="body1"
                                sx={{
                                    fontFamily: 'monospace',
                                    color: 'text.primary',
                                    px: 0.25,
                                }}
                            >
                                {token.text}
                            </Typography>
                        );
                    }
                })}
            </Box>
            {testMode && isValid && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderTop: '1px solid',
                        borderTopColor: 'divider',
                        pt: 1,
                        mt: 1,
                    }}
                >
                    <Typography variant="body2">Result: </Typography>
                    <FormulaResult isCalculating={isCalculating} error={calculationError} result={calculatedResult} />
                </Box>
            )}
        </Box>
    );
};

export default FormulaValidator;
